// PackagingAnalysisPage.js - Updated with Pagination, Search, and Filters (consistent with other pages)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { db } from '../../firebaseConfig';
import { getAuth } from 'firebase/auth';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    getDoc,
    writeBatch
} from 'firebase/firestore';
import styles from '../Registers/Registers.module.css';

const EXPIRY_STATUS = {
    PENDING_REVIEW: 'PENDING_REVIEW',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    APPROVED_WITH_RESTRICTION: 'APPROVED_WITH_RESTRICTION'
};

const ITEMS_PER_PAGE = 10;

const PackagingAnalysisPage = () => {
    const [pendingItems, setPendingItems] = useState([]);
    const [analyzedItems, setAnalyzedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUserName, setCurrentUserName] = useState('Carregando...');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(ITEMS_PER_PAGE);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    const auth = getAuth();
    const currentUser = auth.currentUser;

    useEffect(() => {
        const fetchUserName = async () => {
            if (!currentUser) {
                setCurrentUserName('Usuário não identificado');
                return;
            }

            try {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    const name = userData.nome || currentUser.displayName || currentUser.email || 'Usuário não identificado';
                    setCurrentUserName(name);
                } else {
                    setCurrentUserName(currentUser.displayName || currentUser.email || 'Usuário não identificado');
                }
            } catch (error) {
                console.error('Erro ao buscar nome do usuário:', error);
                setCurrentUserName(currentUser.displayName || currentUser.email || 'Usuário não identificado');
            }
        };

        fetchUserName();
    }, [currentUser]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });
    const [fetchingSupplier, setFetchingSupplier] = useState(false);
    const [lastAnalysisData, setLastAnalysisData] = useState(null);
    const [generatingDoc, setGeneratingDoc] = useState(false);

    const calculateFiveYearsFromNow = () => {
        const today = new Date();
        const fiveYearsLater = new Date(today);
        fiveYearsLater.setFullYear(today.getFullYear() + 5);

        const day = String(fiveYearsLater.getDate()).padStart(2, '0');
        const month = String(fiveYearsLater.getMonth() + 1).padStart(2, '0');
        const year = fiveYearsLater.getFullYear();

        return `${day}/${month}/${year}`;
    };

    const convertToFirebaseDate = (dateStr) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/');
        return `${year}-${month}-${day}`;
    };

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return '—';
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    const formatDateFromAny = (val) => {
        if (!val) return '';
        if (val?.seconds) return new Date(val.seconds * 1000).toLocaleDateString('pt-BR');
        if (val instanceof Date) return val.toLocaleDateString('pt-BR');
        if (typeof val === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                const [y, m, d] = val.split('-');
                return `${d}/${m}/${y}`;
            }
            return val;
        }
        return '';
    };

    const [formData, setFormData] = useState({
        analysisType: 'Frasco',
        nf: '',
        pedidoCompra: '',
        qtdAmostra: '',
        liberacao: 'Aprovado',
        observacao: '',
        newExpiryDate: calculateFiveYearsFromNow(),

        frasco_forma_especificacao: 'CONF. AMOSTRA',
        frasco_forma_tolerancia: 'SEM ERROS',
        frasco_forma_encontrado: '',

        frasco_cor_especificacao: 'CONF. AMOSTRA',
        frasco_cor_tolerancia: 'SEM ERROS',
        frasco_cor_encontrado: '',

        frasco_serigrafia_especificacao: 'CONF. AMOSTRA',
        frasco_serigrafia_tolerancia: 'SEM ERROS',
        frasco_serigrafia_encontrado: '',

        frasco_peso_especificacao: '',
        frasco_peso_tolerancia: '',
        frasco_peso_encontrado: '',

        frasco_diametro_especificacao: '',
        frasco_diametro_tolerancia: '',
        frasco_diametro_encontrado: '',

        frasco_rosqueamento_especificacao: '',
        frasco_rosqueamento_tolerancia: 'SEM ERROS',
        frasco_rosqueamento_encontrado: '',

        frasco_vedacao_especificacao: '',
        frasco_vedacao_tolerancia: 'SEM ERROS',
        frasco_vedacao_encontrado: '',

        frasco_rebarbas_especificacao: 'SEM REBARBAS',
        frasco_rebarbas_tolerancia: 'SEM ERROS',
        frasco_rebarbas_encontrado: '',

        tampa_cor_especificacao: 'CONF. AMOSTRA',
        tampa_cor_tolerancia: 'PADRÃO',
        tampa_cor_encontrado: '',

        tampa_peso_especificacao: '',
        tampa_peso_tolerancia: '',
        tampa_peso_encontrado: '',

        tampa_rosqueamento_especificacao: 'CONF. AMOSTRA',
        tampa_rosqueamento_tolerancia: 'SEM ERROS',
        tampa_rosqueamento_encontrado: '',

        tampa_vedacao_especificacao: 'CONF. AMOSTRA',
        tampa_vedacao_tolerancia: 'SEM ERROS',
        tampa_vedacao_encontrado: '',

        tampa_rebarbas_especificacao: 'SEM REBARBAS',
        tampa_rebarbas_tolerancia: 'SEM ERROS',
        tampa_rebarbas_encontrado: '',

        rotulo_arte_especificacao: '',
        rotulo_arte_tolerancia: 'PADRÃO',
        rotulo_arte_encontrado: '',

        rotulo_codigo_especificacao: '',
        rotulo_codigo_tolerancia: 'NÃO APLICÁVEL',
        rotulo_codigo_encontrado: '',

        rotulo_legibilidade_especificacao: '',
        rotulo_legibilidade_tolerancia: 'SEM ERROS',
        rotulo_legibilidade_encontrado: '',

        rotulo_falhas_especificacao: '',
        rotulo_falhas_tolerancia: 'SEM ERROS',
        rotulo_falhas_encontrado: ''
    });

    useEffect(() => {
        if (formData.liberacao === 'Aprovado' || formData.liberacao === 'Aprovado com restrição') {
            setFormData(prev => ({
                ...prev,
                newExpiryDate: calculateFiveYearsFromNow()
            }));
        }
    }, [formData.liberacao]);

    useEffect(() => {
        const pendingQuery = query(
            collection(db, 'current-stock'),
            where('expiryStatus', '==', EXPIRY_STATUS.PENDING_REVIEW)
        );

        const analyzedQuery = query(
            collection(db, 'current-stock'),
            where('expiryStatus', 'in', [
                EXPIRY_STATUS.APPROVED,
                EXPIRY_STATUS.REJECTED,
                EXPIRY_STATUS.APPROVED_WITH_RESTRICTION
            ])
        );

        const unsubscribePending = onSnapshot(pendingQuery, (snapshot) => {
            const itemsMap = new Map();

            snapshot.forEach((document) => {
                const data = document.data();
                const key = `${data.rawMaterialId}_${data.lotNumber || 'SEM_LOTE'}`;

                if (!itemsMap.has(key)) {
                    itemsMap.set(key, {
                        rawMaterialId: data.rawMaterialId,
                        rawMaterialCode: data.rawMaterialCode,
                        rawMaterialName: data.rawMaterialName,
                        lotNumber: data.lotNumber || 'N/A',
                        currentExpiryDate: data.expirationDate,
                        supplier: 'Buscando...',
                        totalQuantity: 0,
                        stockIds: [],
                        status: 'pending',
                        lastAnalysis: data.lastAnalysis || null
                    });
                }

                const group = itemsMap.get(key);
                group.totalQuantity += Number(data.quantity) || 0;
                group.stockIds.push(document.id);
            });

            setPendingItems(Array.from(itemsMap.values()));
            setLoading(false);
        }, (error) => {
            console.error("Erro na busca de lotes pendentes:", error);
            setLoading(false);
        });

        const unsubscribeAnalyzed = onSnapshot(analyzedQuery, (snapshot) => {
            const itemsMap = new Map();

            snapshot.forEach((document) => {
                const data = document.data();
                const key = `${data.rawMaterialId}_${data.lotNumber || 'SEM_LOTE'}`;

                if (!itemsMap.has(key)) {
                    itemsMap.set(key, {
                        rawMaterialId: data.rawMaterialId,
                        rawMaterialCode: data.rawMaterialCode,
                        rawMaterialName: data.rawMaterialName,
                        lotNumber: data.lotNumber || 'N/A',
                        currentExpiryDate: data.expirationDate,
                        supplier: 'Buscando...',
                        totalQuantity: 0,
                        stockIds: [],
                        status: 'analyzed',
                        lastAnalysis: data.lastAnalysis || null,
                        expiryStatus: data.expiryStatus
                    });
                }

                const group = itemsMap.get(key);
                group.totalQuantity += Number(data.quantity) || 0;
                group.stockIds.push(document.id);
            });

            setAnalyzedItems(Array.from(itemsMap.values()));
        }, (error) => {
            console.error("Erro na busca de lotes analisados:", error);
        });

        return () => {
            unsubscribePending();
            unsubscribeAnalyzed();
        };
    }, []);

    // Combine and filter items
    const filteredItems = useMemo(() => {
        const combined = [...pendingItems, ...analyzedItems];
        return combined.filter(item => {
            // Search filter
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch =
                item.rawMaterialCode?.toLowerCase().includes(searchLower) ||
                item.rawMaterialName?.toLowerCase().includes(searchLower) ||
                item.lotNumber?.toLowerCase().includes(searchLower) ||
                item.supplier?.toLowerCase().includes(searchLower);

            // Status filter
            let matchesStatus = true;
            if (statusFilter === 'pending') {
                matchesStatus = item.status === 'pending';
            } else if (statusFilter === 'approved') {
                matchesStatus = item.status === 'analyzed' && item.expiryStatus === EXPIRY_STATUS.APPROVED;
            } else if (statusFilter === 'rejected') {
                matchesStatus = item.status === 'analyzed' && item.expiryStatus === EXPIRY_STATUS.REJECTED;
            } else if (statusFilter === 'restricted') {
                matchesStatus = item.status === 'analyzed' && item.expiryStatus === EXPIRY_STATUS.APPROVED_WITH_RESTRICTION;
            }

            // Type filter (based on last analysis type)
            let matchesType = true;
            if (typeFilter !== 'all') {
                const analysisType = item.lastAnalysis?.analysisType || '';
                matchesType = analysisType === typeFilter;
            }

            return matchesSearch && matchesStatus && matchesType;
        });
    }, [pendingItems, analyzedItems, searchTerm, statusFilter, typeFilter]);

    // Pagination calculations
    const totalFilteredItems = filteredItems.length;
    const totalPages = Math.ceil(totalFilteredItems / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, typeFilter]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleOpenModal = async (item) => {
        setSelectedItem(item);
        setFetchingSupplier(true);
        setIsModalOpen(true);
        setStatusMsg({ type: '', text: '' });
        setLastAnalysisData(null);

        const fiveYearsFromNow = calculateFiveYearsFromNow();

        setFormData({
            analysisType: 'Frasco',
            nf: '',
            pedidoCompra: '',
            qtdAmostra: '',
            liberacao: 'Aprovado',
            observacao: '',
            newExpiryDate: fiveYearsFromNow,

            frasco_forma_especificacao: 'CONF. AMOSTRA',
            frasco_forma_tolerancia: 'SEM ERROS',
            frasco_forma_encontrado: '',
            frasco_cor_especificacao: 'CONF. AMOSTRA',
            frasco_cor_tolerancia: 'SEM ERROS',
            frasco_cor_encontrado: '',
            frasco_serigrafia_especificacao: 'CONF. AMOSTRA',
            frasco_serigrafia_tolerancia: 'SEM ERROS',
            frasco_serigrafia_encontrado: '',
            frasco_peso_especificacao: '',
            frasco_peso_tolerancia: '',
            frasco_peso_encontrado: '',
            frasco_diametro_especificacao: '',
            frasco_diametro_tolerancia: '',
            frasco_diametro_encontrado: '',
            frasco_rosqueamento_especificacao: '',
            frasco_rosqueamento_tolerancia: 'SEM ERROS',
            frasco_rosqueamento_encontrado: '',
            frasco_vedacao_especificacao: '',
            frasco_vedacao_tolerancia: 'SEM ERROS',
            frasco_vedacao_encontrado: '',
            frasco_rebarbas_especificacao: 'SEM REBARBAS',
            frasco_rebarbas_tolerancia: 'SEM ERROS',
            frasco_rebarbas_encontrado: '',

            tampa_cor_especificacao: 'CONF. AMOSTRA',
            tampa_cor_tolerancia: 'PADRÃO',
            tampa_cor_encontrado: '',
            tampa_peso_especificacao: '',
            tampa_peso_tolerancia: '',
            tampa_peso_encontrado: '',
            tampa_rosqueamento_especificacao: 'CONF. AMOSTRA',
            tampa_rosqueamento_tolerancia: 'SEM ERROS',
            tampa_rosqueamento_encontrado: '',
            tampa_vedacao_especificacao: 'CONF. AMOSTRA',
            tampa_vedacao_tolerancia: 'SEM ERROS',
            tampa_vedacao_encontrado: '',
            tampa_rebarbas_especificacao: 'SEM REBARBAS',
            tampa_rebarbas_tolerancia: 'SEM ERROS',
            tampa_rebarbas_encontrado: '',

            rotulo_arte_especificacao: '',
            rotulo_arte_tolerancia: 'PADRÃO',
            rotulo_arte_encontrado: '',
            rotulo_codigo_especificacao: '',
            rotulo_codigo_tolerancia: 'NÃO APLICÁVEL',
            rotulo_codigo_encontrado: '',
            rotulo_legibilidade_especificacao: '',
            rotulo_legibilidade_tolerancia: 'SEM ERROS',
            rotulo_legibilidade_encontrado: '',
            rotulo_falhas_especificacao: '',
            rotulo_falhas_tolerancia: 'SEM ERROS',
            rotulo_falhas_encontrado: ''
        });

        let foundSupplier = 'Não informado';
        try {
            const rmDocRef = doc(db, 'raw-materials', item.rawMaterialId);
            const rmDocSnap = await getDoc(rmDocRef);

            if (rmDocSnap.exists()) {
                const rmData = rmDocSnap.data();
                if (rmData.suppliers && rmData.suppliers.length > 0) {
                    foundSupplier = rmData.suppliers[0].razaoSocial || rmData.suppliers[0].nome || 'Fornecedor sem Razão Social';
                }
            }
        } catch (error) {
            console.error("Incapaz de recuperar a Razão Social do fornecedor:", error);
        }

        setSelectedItem(prev => ({ ...prev, supplier: foundSupplier }));
        setFetchingSupplier(false);
    };

    const handleViewAnalysis = (item) => {
        if (item.lastAnalysis) {
            setSelectedItem(item);
            setLastAnalysisData(item.lastAnalysis);
            setIsModalOpen(true);
            setStatusMsg({ type: '', text: '' });

            const analysis = item.lastAnalysis;
            setFormData({
                analysisType: analysis.analysisType || 'Frasco',
                nf: analysis.nf || '',
                pedidoCompra: analysis.pedidoCompra || '',
                qtdAmostra: analysis.qtdAmostra || '',
                liberacao: analysis.liberacao || 'Aprovado',
                observacao: analysis.observacao || '',
                newExpiryDate: analysis.newExpiryDate ? formatDisplayDate(analysis.newExpiryDate) : calculateFiveYearsFromNow(),
                ...analysis.inspeccaoCampos?.frasco && {
                    frasco_forma_especificacao: analysis.inspeccaoCampos.frasco.forma_especificacao || '',
                    frasco_forma_tolerancia: analysis.inspeccaoCampos.frasco.forma_tolerancia || '',
                    frasco_forma_encontrado: analysis.inspeccaoCampos.frasco.forma_encontrado || '',
                    frasco_cor_especificacao: analysis.inspeccaoCampos.frasco.cor_especificacao || '',
                    frasco_cor_tolerancia: analysis.inspeccaoCampos.frasco.cor_tolerancia || '',
                    frasco_cor_encontrado: analysis.inspeccaoCampos.frasco.cor_encontrado || '',
                    frasco_serigrafia_especificacao: analysis.inspeccaoCampos.frasco.serigrafia_especificacao || '',
                    frasco_serigrafia_tolerancia: analysis.inspeccaoCampos.frasco.serigrafia_tolerancia || '',
                    frasco_serigrafia_encontrado: analysis.inspeccaoCampos.frasco.serigrafia_encontrado || '',
                    frasco_peso_especificacao: analysis.inspeccaoCampos.frasco.peso_especificacao || '',
                    frasco_peso_tolerancia: analysis.inspeccaoCampos.frasco.peso_tolerancia || '',
                    frasco_peso_encontrado: analysis.inspeccaoCampos.frasco.peso_encontrado || '',
                    frasco_diametro_especificacao: analysis.inspeccaoCampos.frasco.diametro_especificacao || '',
                    frasco_diametro_tolerancia: analysis.inspeccaoCampos.frasco.diametro_tolerancia || '',
                    frasco_diametro_encontrado: analysis.inspeccaoCampos.frasco.diametro_encontrado || '',
                    frasco_rosqueamento_especificacao: analysis.inspeccaoCampos.frasco.rosqueamento_especificacao || '',
                    frasco_rosqueamento_tolerancia: analysis.inspeccaoCampos.frasco.rosqueamento_tolerancia || '',
                    frasco_rosqueamento_encontrado: analysis.inspeccaoCampos.frasco.rosqueamento_encontrado || '',
                    frasco_vedacao_especificacao: analysis.inspeccaoCampos.frasco.vedacao_especificacao || '',
                    frasco_vedacao_tolerancia: analysis.inspeccaoCampos.frasco.vedacao_tolerancia || '',
                    frasco_vedacao_encontrado: analysis.inspeccaoCampos.frasco.vedacao_encontrado || '',
                    frasco_rebarbas_especificacao: analysis.inspeccaoCampos.frasco.rebarbas_especificacao || '',
                    frasco_rebarbas_tolerancia: analysis.inspeccaoCampos.frasco.rebarbas_tolerancia || '',
                    frasco_rebarbas_encontrado: analysis.inspeccaoCampos.frasco.rebarbas_encontrado || '',
                },
                ...analysis.inspeccaoCampos?.tampa && {
                    tampa_cor_especificacao: analysis.inspeccaoCampos.tampa.cor_especificacao || '',
                    tampa_cor_tolerancia: analysis.inspeccaoCampos.tampa.cor_tolerancia || '',
                    tampa_cor_encontrado: analysis.inspeccaoCampos.tampa.cor_encontrado || '',
                    tampa_peso_especificacao: analysis.inspeccaoCampos.tampa.peso_especificacao || '',
                    tampa_peso_tolerancia: analysis.inspeccaoCampos.tampa.peso_tolerancia || '',
                    tampa_peso_encontrado: analysis.inspeccaoCampos.tampa.peso_encontrado || '',
                    tampa_rosqueamento_especificacao: analysis.inspeccaoCampos.tampa.rosqueamento_especificacao || '',
                    tampa_rosqueamento_tolerancia: analysis.inspeccaoCampos.tampa.rosqueamento_tolerancia || '',
                    tampa_rosqueamento_encontrado: analysis.inspeccaoCampos.tampa.rosqueamento_encontrado || '',
                    tampa_vedacao_especificacao: analysis.inspeccaoCampos.tampa.vedacao_especificacao || '',
                    tampa_vedacao_tolerancia: analysis.inspeccaoCampos.tampa.vedacao_tolerancia || '',
                    tampa_vedacao_encontrado: analysis.inspeccaoCampos.tampa.vedacao_encontrado || '',
                    tampa_rebarbas_especificacao: analysis.inspeccaoCampos.tampa.rebarbas_especificacao || '',
                    tampa_rebarbas_tolerancia: analysis.inspeccaoCampos.tampa.rebarbas_tolerancia || '',
                    tampa_rebarbas_encontrado: analysis.inspeccaoCampos.tampa.rebarbas_encontrado || '',
                },
                ...analysis.inspeccaoCampos?.rotulo && {
                    rotulo_arte_especificacao: analysis.inspeccaoCampos.rotulo.arte_especificacao || '',
                    rotulo_arte_tolerancia: analysis.inspeccaoCampos.rotulo.arte_tolerancia || '',
                    rotulo_arte_encontrado: analysis.inspeccaoCampos.rotulo.arte_encontrado || '',
                    rotulo_codigo_especificacao: analysis.inspeccaoCampos.rotulo.codigo_especificacao || '',
                    rotulo_codigo_tolerancia: analysis.inspeccaoCampos.rotulo.codigo_tolerancia || '',
                    rotulo_codigo_encontrado: analysis.inspeccaoCampos.rotulo.codigo_encontrado || '',
                    rotulo_legibilidade_especificacao: analysis.inspeccaoCampos.rotulo.legibilidade_especificacao || '',
                    rotulo_legibilidade_tolerancia: analysis.inspeccaoCampos.rotulo.legibilidade_tolerancia || '',
                    rotulo_legibilidade_encontrado: analysis.inspeccaoCampos.rotulo.legibilidade_encontrado || '',
                    rotulo_falhas_especificacao: analysis.inspeccaoCampos.rotulo.falhas_especificacao || '',
                    rotulo_falhas_tolerancia: analysis.inspeccaoCampos.rotulo.falhas_tolerancia || '',
                    rotulo_falhas_encontrado: analysis.inspeccaoCampos.rotulo.falhas_encontrado || '',
                }
            });
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedItem(null);
        setLastAnalysisData(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // -------------------------------------------------------
    // FQ-18 Template Generation
    // -------------------------------------------------------
    const generateFQ18 = async (item, analysisData) => {
        let supplierName = analysisData.supplier || '';
        if (!supplierName) {
            try {
                const rmDocRef = doc(db, 'raw-materials', item.rawMaterialId);
                const rmDocSnap = await getDoc(rmDocRef);
                if (rmDocSnap.exists()) {
                    const rmData = rmDocSnap.data();
                    if (rmData.suppliers?.length > 0) {
                        supplierName = rmData.suppliers[0].razaoSocial || rmData.suppliers[0].nome || '';
                    }
                }
            } catch (e) {
                console.warn('Could not fetch supplier for document:', e);
            }
        }

        const response = await fetch('/templates/FQ-18.xlsx');
        if (!response.ok) {
            throw new Error('Template FQ-18.xlsx não encontrado em /public/templates/');
        }
        const templateBuffer = await response.arrayBuffer();

        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(templateBuffer);
        const ws = wb.getWorksheet('PRENSA 01');

        const set = (coord, value) => {
            ws.getCell(coord).value = value;
        };

        const campos = analysisData.inspeccaoCampos || {};
        const frasco = campos.frasco || {};
        const tampa = campos.tampa || {};
        const rotulo = campos.rotulo || {};
        const analysisType = analysisData.analysisType || 'Frasco';
        const liberacao = analysisData.liberacao || '';

        const typeMarkers = {
            'Frasco': / \(    \) FRASCO/,
            'Tampa': /\(     \)  TAMPA/,
            'Rótulo': /\(     \)  RÓTULO/,
            'Revalidação': /\(   \) REVALIDAÇÃO/,
        };
        let typeText = ws.getCell('B4').value || '';
        const markerToReplace = typeMarkers[analysisType];
        if (markerToReplace) {
            typeText = typeText.replace(markerToReplace, (m) => m.replace(/\(\s*\)/, '( X )'));
        }
        set('B4', typeText);

        set('B5', `PRODUTO: ${item.rawMaterialName || ''}`);
        set('F5', `LOTE: ${item.lotNumber || ''}`);
        set('H5', `CÓDIGO: ${item.rawMaterialCode || ''}`);

        set('B6', `FORNECEDOR: ${supplierName}`);
        set('F6', `N.F.: ${analysisData.nf || ''}`);
        set('H6', `PEDIDO DE COMPRA: ${analysisData.pedidoCompra || ''}`);

        set('B7', `QUANTIDADE TOTAL: ${item.totalQuantity?.toLocaleString('pt-BR') || ''}`);
        set('F7', `QUANTIDADE DE AMOSTRA (CONFORME INSTRUÇÃO IT-12): ${analysisData.qtdAmostra || ''}`);

        const inspectionDate = formatDateFromAny(analysisData.analyzedAt) || new Date().toLocaleDateString('pt-BR');
        set('B8', `INSPECIONADO POR: ${analysisData.analyzedBy || ''}`);
        set('H8', `DATA DA INSPEÇÃO: ${inspectionDate}`);

        set('E11', frasco.forma_especificacao || '');
        set('G11', frasco.forma_tolerancia || '');
        set('I11', frasco.forma_encontrado || '');

        set('E12', frasco.cor_especificacao || '');
        set('G12', frasco.cor_tolerancia || '');
        set('I12', frasco.cor_encontrado || '');

        set('E13', frasco.serigrafia_especificacao || '');
        set('G13', frasco.serigrafia_tolerancia || '');
        set('I13', frasco.serigrafia_encontrado || '');

        set('E14', frasco.peso_especificacao || '');
        set('G14', frasco.peso_tolerancia || '');
        set('I14', frasco.peso_encontrado || '');

        set('E15', frasco.diametro_especificacao || '');
        set('G15', frasco.diametro_tolerancia || '');
        set('I15', frasco.diametro_encontrado || '');

        set('E16', frasco.rosqueamento_especificacao || '');
        set('G16', frasco.rosqueamento_tolerancia || '');
        set('I16', frasco.rosqueamento_encontrado || '');

        set('E17', frasco.vedacao_especificacao || '');
        set('G17', frasco.vedacao_tolerancia || '');
        set('I17', frasco.vedacao_encontrado || '');

        set('E18', frasco.rebarbas_especificacao || '');
        set('G18', frasco.rebarbas_tolerancia || '');
        set('I18', frasco.rebarbas_encontrado || '');

        set('E21', tampa.cor_especificacao || '');
        set('G21', tampa.cor_tolerancia || '');
        set('I21', tampa.cor_encontrado || '');

        set('E22', tampa.peso_especificacao || '');
        set('G22', tampa.peso_tolerancia || '');
        set('I22', tampa.peso_encontrado || '');

        set('E23', tampa.rosqueamento_especificacao || '');
        set('G23', tampa.rosqueamento_tolerancia || '');
        set('I23', tampa.rosqueamento_encontrado || '');

        set('E24', tampa.vedacao_especificacao || '');
        set('G24', tampa.vedacao_tolerancia || '');
        set('I24', tampa.vedacao_encontrado || '');

        set('E25', tampa.rebarbas_especificacao || '');
        set('G25', tampa.rebarbas_tolerancia || '');
        set('I25', tampa.rebarbas_encontrado || '');

        set('E28', rotulo.arte_especificacao || '');
        set('G28', rotulo.arte_tolerancia || '');
        set('I28', rotulo.arte_encontrado || '');

        set('E29', rotulo.codigo_especificacao || '');
        set('G29', rotulo.codigo_tolerancia || '');
        set('I29', rotulo.codigo_encontrado || '');

        set('E30', rotulo.legibilidade_especificacao || '');
        set('G30', rotulo.legibilidade_tolerancia || '');
        set('I30', rotulo.legibilidade_encontrado || '');

        set('E31', rotulo.falhas_especificacao || '');
        set('G31', rotulo.falhas_tolerancia || '');
        set('I31', rotulo.falhas_encontrado || '');

        const newExpiryFormatted = formatDateFromAny(analysisData.newExpiryDate);
        let libText = 'APROVADO (    )          REPROVADO (    )      APROVADO COM RESTRIÇÃO (    )\n\n';
        if (newExpiryFormatted) {
            libText += `Nova Data de Validade: ${newExpiryFormatted}`;
        }
        if (liberacao === 'Aprovado') {
            libText = libText.replace('APROVADO (    )', 'APROVADO ( X )');
        } else if (liberacao === 'Reprovado') {
            libText = libText.replace('REPROVADO (    )', 'REPROVADO ( X )');
        } else if (liberacao === 'Aprovado com restrição') {
            libText = libText.replace('APROVADO COM RESTRIÇÃO (    )', 'APROVADO COM RESTRIÇÃO ( X )');
        }
        set('B34', libText);

        set('B35', `OBSERVAÇÃO: ${analysisData.observacao || ''}`);

        set('B42', `                                        ${analysisData.analyzedBy || currentUserName}          DATA: ${inspectionDate}`);

        const buffer = await wb.xlsx.writeBuffer();
        return buffer;
    };

    const handleGenerateTemplate = async (item) => {
        let analysisData = item.lastAnalysis;

        if (!analysisData) {
            analysisData = await getAnalysisData(item);
        }

        if (!analysisData) {
            setStatusMsg({ type: 'error', text: 'Nenhum dado de análise encontrado para gerar o FQ-18.' });
            return;
        }

        setGeneratingDoc(true);
        setStatusMsg({ type: '', text: '' });

        try {
            const buffer = await generateFQ18(item, analysisData);
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const safeCode = (item.rawMaterialCode || 'REL').replace(/[^a-zA-Z0-9]/g, '_');
            const safeLot = (item.lotNumber || 'LOTE').replace(/[^a-zA-Z0-9]/g, '_');
            const dateTag = new Date().toISOString().slice(0, 10);
            const fileName = `FQ-18_${safeCode}_${safeLot}_${dateTag}.xlsx`;

            saveAs(blob, fileName);
            setStatusMsg({ type: 'success', text: `FQ-18 gerado com sucesso! Arquivo: ${fileName}` });
            setTimeout(() => setStatusMsg({ type: '', text: '' }), 4000);
        } catch (error) {
            console.error('Erro ao gerar FQ-18:', error);
            setStatusMsg({ type: 'error', text: `Erro ao gerar FQ-18: ${error.message}` });
        } finally {
            setGeneratingDoc(false);
        }
    };

    const getAnalysisData = async (item) => {
        if (item.lastAnalysis) return item.lastAnalysis;

        try {
            const stockDoc = await getDoc(doc(db, 'current-stock', item.stockIds[0]));
            if (stockDoc.exists()) {
                const data = stockDoc.data();
                return data.lastAnalysis || null;
            }
        } catch (error) {
            console.error('Erro ao buscar dados de análise:', error);
        }
        return null;
    };

    const handleSubmitAnalysis = async (e) => {
        e.preventDefault();

        const isApproved = formData.liberacao === 'Aprovado' || formData.liberacao === 'Aprovado com restrição';

        if (isApproved && !formData.newExpiryDate) {
            setStatusMsg({ type: 'error', text: 'Por favor, estipule a nova data de validade para a liberação deste lote.' });
            return;
        }

        setSubmitting(true);
        setStatusMsg({ type: '', text: '' });

        try {
            const batch = writeBatch(db);
            const now = new Date();

            let finalStatus;
            if (formData.liberacao === 'Aprovado') finalStatus = EXPIRY_STATUS.APPROVED;
            else if (formData.liberacao === 'Aprovado com restrição') finalStatus = EXPIRY_STATUS.APPROVED_WITH_RESTRICTION;
            else finalStatus = EXPIRY_STATUS.REJECTED;

            const formattedExpiryDate = convertToFirebaseDate(formData.newExpiryDate);

            const analysisRecord = {
                template: 'FQ-18',
                analysisType: formData.analysisType,
                nf: formData.nf,
                pedidoCompra: formData.pedidoCompra,
                qtdAmostra: formData.qtdAmostra,
                supplier: selectedItem.supplier !== 'Buscando...' ? selectedItem.supplier : '',
                inspeccaoCampos: {
                    frasco: {
                        forma_especificacao: formData.frasco_forma_especificacao,
                        forma_tolerancia: formData.frasco_forma_tolerancia,
                        forma_encontrado: formData.frasco_forma_encontrado,
                        cor_especificacao: formData.frasco_cor_especificacao,
                        cor_tolerancia: formData.frasco_cor_tolerancia,
                        cor_encontrado: formData.frasco_cor_encontrado,
                        serigrafia_especificacao: formData.frasco_serigrafia_especificacao,
                        serigrafia_tolerancia: formData.frasco_serigrafia_tolerancia,
                        serigrafia_encontrado: formData.frasco_serigrafia_encontrado,
                        peso_especificacao: formData.frasco_peso_especificacao,
                        peso_tolerancia: formData.frasco_peso_tolerancia,
                        peso_encontrado: formData.frasco_peso_encontrado,
                        diametro_especificacao: formData.frasco_diametro_especificacao,
                        diametro_tolerancia: formData.frasco_diametro_tolerancia,
                        diametro_encontrado: formData.frasco_diametro_encontrado,
                        rosqueamento_especificacao: formData.frasco_rosqueamento_especificacao,
                        rosqueamento_tolerancia: formData.frasco_rosqueamento_tolerancia,
                        rosqueamento_encontrado: formData.frasco_rosqueamento_encontrado,
                        vedacao_especificacao: formData.frasco_vedacao_especificacao,
                        vedacao_tolerancia: formData.frasco_vedacao_tolerancia,
                        vedacao_encontrado: formData.frasco_vedacao_encontrado,
                        rebarbas_especificacao: formData.frasco_rebarbas_especificacao,
                        rebarbas_tolerancia: formData.frasco_rebarbas_tolerancia,
                        rebarbas_encontrado: formData.frasco_rebarbas_encontrado,
                    },
                    tampa: {
                        cor_especificacao: formData.tampa_cor_especificacao,
                        cor_tolerancia: formData.tampa_cor_tolerancia,
                        cor_encontrado: formData.tampa_cor_encontrado,
                        peso_especificacao: formData.tampa_peso_especificacao,
                        peso_tolerancia: formData.tampa_peso_tolerancia,
                        peso_encontrado: formData.tampa_peso_encontrado,
                        rosqueamento_especificacao: formData.tampa_rosqueamento_especificacao,
                        rosqueamento_tolerancia: formData.tampa_rosqueamento_tolerancia,
                        rosqueamento_encontrado: formData.tampa_rosqueamento_encontrado,
                        vedacao_especificacao: formData.tampa_vedacao_especificacao,
                        vedacao_tolerancia: formData.tampa_vedacao_tolerancia,
                        vedacao_encontrado: formData.tampa_vedacao_encontrado,
                        rebarbas_especificacao: formData.tampa_rebarbas_especificacao,
                        rebarbas_tolerancia: formData.tampa_rebarbas_tolerancia,
                        rebarbas_encontrado: formData.tampa_rebarbas_encontrado,
                    },
                    rotulo: {
                        arte_especificacao: formData.rotulo_arte_especificacao,
                        arte_tolerancia: formData.rotulo_arte_tolerancia,
                        arte_encontrado: formData.rotulo_arte_encontrado,
                        codigo_especificacao: formData.rotulo_codigo_especificacao,
                        codigo_tolerancia: formData.rotulo_codigo_tolerancia,
                        codigo_encontrado: formData.rotulo_codigo_encontrado,
                        legibilidade_especificacao: formData.rotulo_legibilidade_especificacao,
                        legibilidade_tolerancia: formData.rotulo_legibilidade_tolerancia,
                        legibilidade_encontrado: formData.rotulo_legibilidade_encontrado,
                        falhas_especificacao: formData.rotulo_falhas_especificacao,
                        falhas_tolerancia: formData.rotulo_falhas_tolerancia,
                        falhas_encontrado: formData.rotulo_falhas_encontrado,
                    }
                },
                liberacao: formData.liberacao,
                observacao: formData.observacao,
                analyzedAt: now,
                analyzedBy: currentUserName,
                newExpiryDate: formattedExpiryDate
            };

            selectedItem.stockIds.forEach(stockId => {
                const stockRef = doc(db, 'current-stock', stockId);
                const updatePayload = {
                    expiryStatus: finalStatus,
                    lastAnalysis: analysisRecord,
                    updatedAt: now
                };

                if (isApproved) {
                    updatePayload.expirationDate = formattedExpiryDate;
                }

                batch.update(stockRef, updatePayload);
            });

            await batch.commit();

            setStatusMsg({ type: 'success', text: 'Análise registrada com sucesso! Gerando FQ-18...' });

            setTimeout(async () => {
                try {
                    const itemForDoc = {
                        ...selectedItem,
                        lastAnalysis: analysisRecord
                    };
                    await handleGenerateTemplate(itemForDoc);
                } catch (genErr) {
                    console.error('Erro na geração automática do FQ-18:', genErr);
                }
                setTimeout(() => handleCloseModal(), 1500);
            }, 500);

        } catch (error) {
            console.error("Falha sistêmica ao salvar laudo de qualidade:", error);
            setStatusMsg({ type: 'error', text: 'Erro ao consolidar laudo de auditoria no banco de dados.' });
        } finally {
            setSubmitting(false);
        }
    };

    const todayFormatted = new Intl.DateTimeFormat('pt-BR').format(new Date());

    // Pagination render helper - same style as ProductsPage and RawMaterialsPage
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        return (
            <div className={styles.paginationControls}>
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={styles.actionButton}
                >
                    ← Anterior
                </button>
                <span style={{ fontWeight: 'bold' }}>
                    Página {currentPage} de {totalPages}
                </span>
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={styles.actionButton}
                >
                    Próxima →
                </button>
            </div>
        );
    };

    return (
        <div className={styles.pageContainer}>
            <header className={styles.header}>
                <div>
                    <h1 className={styles.headerTitle}>Análise de Embalagens</h1>
                    <p style={{ color: 'var(--text-secondary)', margin: '5px 0 0 0' }}>
                        Módulo de Liberação Técnica e Extensão de Prazos de Validade
                    </p>
                </div>
            </header>

            {/* Search and Filters Bar - same style as other pages */}
            <div className={styles.searchBar}>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div className={styles.inputGroup} style={{ flex: '1' }}>
                        <label>Buscar</label>
                        <input
                            type="text"
                            placeholder="Código, produto, lote ou fornecedor..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                        <label>Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            className={styles.select}
                        >
                            <option value="all">Todos</option>
                            <option value="pending">⏳ Pendente</option>
                            <option value="approved">✅ Aprovado</option>
                            <option value="restricted">⚠️ Aprovado c/ Restrição</option>
                            <option value="rejected">❌ Reprovado</option>
                        </select>
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                        <label>Tipo de Análise</label>
                        <select
                            value={typeFilter}
                            onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                            className={styles.select}
                        >
                            <option value="all">Todos</option>
                            <option value="Frasco">Frasco</option>
                            <option value="Tampa">Tampa</option>
                            <option value="Rótulo">Rótulo</option>
                            <option value="Revalidação">Revalidação</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '120px' }}>Código</th>
                            <th>Insumo / Componente</th>
                            <th style={{ width: '150px' }}>Lote</th>
                            <th style={{ width: '150px' }}>Vencimento Atual</th>
                            <th style={{ width: '150px', textAlign: 'right' }}>Quantidade</th>
                            <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                            <th style={{ width: '140px', textAlign: 'center' }}>Ação</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" className={styles.emptyTable}>Rastreando lotes...</td></tr>
                        ) : currentItems.length === 0 ? (
                            <tr><td colSpan="7" className={styles.emptyTable}>
                                {filteredItems.length === 0
                                    ? 'Nenhum lote de embalagem cadastrado no sistema.'
                                    : 'Nenhum item corresponde aos filtros aplicados.'}
                            </td></tr>
                        ) : (
                            currentItems.map((item) => {
                                const isPending = item.status === 'pending';
                                const statusLabel = isPending ? 'Pendente' :
                                    item.expiryStatus === EXPIRY_STATUS.APPROVED ? 'Aprovado' :
                                        item.expiryStatus === EXPIRY_STATUS.APPROVED_WITH_RESTRICTION ? 'Aprovado c/ Restrição' :
                                            'Reprovado';
                                const statusColor = isPending ? 'var(--warning-color)' :
                                    item.expiryStatus === EXPIRY_STATUS.APPROVED ? 'var(--success-color)' :
                                        item.expiryStatus === EXPIRY_STATUS.APPROVED_WITH_RESTRICTION ? 'var(--warning-color)' :
                                            'var(--danger-color)';

                                return (
                                    <tr key={`${item.rawMaterialId}_${item.lotNumber}`}>
                                        <td><strong>{item.rawMaterialCode}</strong></td>
                                        <td>{item.rawMaterialName}</td>
                                        <td><span className={styles.badgeLote}>{item.lotNumber}</span></td>
                                        <td style={{ color: isPending ? 'var(--danger-color)' : 'var(--text-secondary)', fontWeight: isPending ? 'bold' : 'normal' }}>
                                            {formatDisplayDate(item.currentExpiryDate)}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {item.totalQuantity.toLocaleString('pt-BR')}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                backgroundColor: statusColor,
                                                color: '#fff',
                                                fontWeight: '600',
                                                fontSize: '0.8rem'
                                            }}>
                                                {statusLabel}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {isPending ? (
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={() => handleOpenModal(item)}
                                                >
                                                    Inspecionar
                                                </button>
                                            ) : (
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={() => handleGenerateTemplate(item)}
                                                    disabled={generatingDoc}
                                                >
                                                    {generatingDoc ? 'Gerando...' : 'Gerar Análise'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {/* Pagination - same style as other pages */}
                {!loading && totalPages > 1 && renderPagination()}
            </div>

            {/* Modal Técnico FQ-18 */}
            {isModalOpen && selectedItem && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.formContainer}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h2>Relatório de Inspeção Qualitativa</h2>
                            </div>
                            <button onClick={handleCloseModal} className={styles.modalCloseButton} disabled={submitting || generatingDoc}>×</button>
                        </div>

                        <form className={styles.formContent} onSubmit={handleSubmitAnalysis}>
                            {statusMsg.text && (
                                <div className={`${styles.alert} ${styles[statusMsg.type]}`}>
                                    {statusMsg.text}
                                </div>
                            )}

                            {/* CABEÇALHO */}
                            <div className={styles.formSection}>
                                <h3 className={styles.sectionTitle}>Metadados de Identificação de Insumo</h3>

                                <div className={styles.radioGroup} style={{ marginBottom: '20px' }}>
                                    {['Frasco', 'Tampa', 'Rótulo', 'Revalidação'].map(type => (
                                        <label key={type} className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="analysisType"
                                                value={type}
                                                checked={formData.analysisType === type}
                                                onChange={handleFormChange}
                                                disabled={!!lastAnalysisData}
                                            />
                                            <strong>{type.toUpperCase()}</strong>
                                        </label>
                                    ))}
                                </div>

                                <div className={styles.formGrid}>
                                    <div className={styles.inputGroup}><label>Produto/Insumo</label><div className={styles.readOnlyField}>{selectedItem.rawMaterialName}</div></div>
                                    <div className={styles.inputGroup}><label>Código Interno</label><div className={styles.readOnlyField}>{selectedItem.rawMaterialCode}</div></div>
                                    <div className={styles.inputGroup}><label>Lote Processado</label><div className={styles.readOnlyField}>{selectedItem.lotNumber}</div></div>
                                    <div className={styles.inputGroup}>
                                        <label>Fornecedor Cadastrado</label>
                                        <div className={styles.readOnlyField}>
                                            {fetchingSupplier ? 'Consultando base mestre...' : selectedItem.supplier}
                                        </div>
                                    </div>
                                    <div className={styles.inputGroup}><label>Quantidade Total em Estoque</label><div className={styles.readOnlyField}>{selectedItem.totalQuantity.toLocaleString('pt-BR')}</div></div>
                                    <div className={styles.inputGroup}><label>Nota Fiscal (N.F.)</label>
                                        <input
                                            type="text"
                                            name="nf"
                                            value={formData.nf}
                                            onChange={handleFormChange}
                                            className={styles.input}
                                            placeholder="A preencher..."
                                            disabled={!!lastAnalysisData}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}><label>Pedido de Compra</label>
                                        <input
                                            type="text"
                                            name="pedidoCompra"
                                            value={formData.pedidoCompra}
                                            onChange={handleFormChange}
                                            className={styles.input}
                                            placeholder="A preencher..."
                                            disabled={!!lastAnalysisData}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}><label>Quantidade de Amostra (IT-12)</label>
                                        <input
                                            type="text"
                                            name="qtdAmostra"
                                            value={formData.qtdAmostra}
                                            onChange={handleFormChange}
                                            className={styles.input}
                                            placeholder="Ex: Conforme plano de amostragem"
                                            disabled={!!lastAnalysisData}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}><label>Inspecionado Por</label>
                                        <div className={styles.readOnlyField}>
                                            {lastAnalysisData ? lastAnalysisData.analyzedBy || currentUserName : currentUserName}
                                        </div>
                                    </div>
                                    <div className={styles.inputGroup}><label>Data de Execução da Inspeção</label>
                                        <div className={styles.readOnlyField}>
                                            {lastAnalysisData ? formatDateFromAny(lastAnalysisData.analyzedAt) : todayFormatted}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* TABELA DE PARÂMETROS */}
                            <div className={styles.formSection}>
                                <h3 className={styles.sectionTitle}>Inspeção Técnica (Tabela de Parâmetros)</h3>
                                <table className={styles.inspectionTable}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '15%' }}>CARACTERÍSTICA</th>
                                            <th style={{ width: '15%' }}></th>
                                            <th style={{ width: '20%' }}>ESPECIFICAÇÃO</th>
                                            <th style={{ width: '20%' }}>TOLERÂNCIA</th>
                                            <th style={{ width: '15%' }}>EQUIPT.</th>
                                            <th style={{ width: '25%' }}>ENCONTRADO</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* FRASCO */}
                                        {(formData.analysisType === 'Frasco' || formData.analysisType === 'Revalidação') && (
                                            <>
                                                <tr>
                                                    <td colSpan="6" className={styles.tableCategory}>FRASCO</td>
                                                </tr>
                                                <tr>
                                                    <td rowSpan="3">ARTE</td>
                                                    <td><strong>FORMA</strong></td>
                                                    <td><input type="text" name="frasco_forma_especificacao" value={formData.frasco_forma_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="CONF. AMOSTRA" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_forma_tolerancia" value={formData.frasco_forma_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="frasco_forma_encontrado" value={formData.frasco_forma_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Conforme / Não conforme..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>COR</strong></td>
                                                    <td><input type="text" name="frasco_cor_especificacao" value={formData.frasco_cor_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="CONF. AMOSTRA" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_cor_tolerancia" value={formData.frasco_cor_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="frasco_cor_encontrado" value={formData.frasco_cor_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Conforme / Não conforme..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>SERIGRAFIA</strong></td>
                                                    <td><input type="text" name="frasco_serigrafia_especificacao" value={formData.frasco_serigrafia_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="CONF. AMOSTRA" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_serigrafia_tolerancia" value={formData.frasco_serigrafia_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="frasco_serigrafia_encontrado" value={formData.frasco_serigrafia_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Conforme / Não conforme..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>PESO</strong></td>
                                                    <td><input type="text" name="frasco_peso_especificacao" value={formData.frasco_peso_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_peso_tolerancia" value={formData.frasco_peso_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td>BALANÇA</td>
                                                    <td><input type="text" name="frasco_peso_encontrado" value={formData.frasco_peso_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Peso medido (g)..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>DIÂMETRO INTERNO BOCAL</strong></td>
                                                    <td><input type="text" name="frasco_diametro_especificacao" value={formData.frasco_diametro_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_diametro_tolerancia" value={formData.frasco_diametro_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td>RÉGUA/PAQUÍMETRO</td>
                                                    <td><input type="text" name="frasco_diametro_encontrado" value={formData.frasco_diametro_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Medição (mm)..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>ROSQUEAMENTO</strong></td>
                                                    <td><input type="text" name="frasco_rosqueamento_especificacao" value={formData.frasco_rosqueamento_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_rosqueamento_tolerancia" value={formData.frasco_rosqueamento_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>MANUAL COM TAMPA</td>
                                                    <td><input type="text" name="frasco_rosqueamento_encontrado" value={formData.frasco_rosqueamento_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Adequado / Inadequado..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>VEDAÇÃO</strong></td>
                                                    <td><input type="text" name="frasco_vedacao_especificacao" value={formData.frasco_vedacao_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_vedacao_tolerancia" value={formData.frasco_vedacao_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>MANUAL COM TAMPA</td>
                                                    <td><input type="text" name="frasco_vedacao_encontrado" value={formData.frasco_vedacao_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Eficaz / Ineficaz..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>REBÁRBAS</strong></td>
                                                    <td><input type="text" name="frasco_rebarbas_especificacao" value={formData.frasco_rebarbas_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM REBARBAS" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="frasco_rebarbas_tolerancia" value={formData.frasco_rebarbas_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="frasco_rebarbas_encontrado" value={formData.frasco_rebarbas_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Ausente / Presente..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                            </>
                                        )}

                                        {/* TAMPA */}
                                        {(formData.analysisType === 'Tampa' || formData.analysisType === 'Revalidação') && (
                                            <>
                                                <tr>
                                                    <td colSpan="6" className={styles.tableCategory}>TAMPA</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>COR</strong></td>
                                                    <td><input type="text" name="tampa_cor_especificacao" value={formData.tampa_cor_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="CONF. AMOSTRA" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="tampa_cor_tolerancia" value={formData.tampa_cor_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="PADRÃO" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="tampa_cor_encontrado" value={formData.tampa_cor_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Conforme / Não conforme..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>PESO</strong></td>
                                                    <td><input type="text" name="tampa_peso_especificacao" value={formData.tampa_peso_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="tampa_peso_tolerancia" value={formData.tampa_peso_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td>BALANÇA</td>
                                                    <td><input type="text" name="tampa_peso_encontrado" value={formData.tampa_peso_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Peso medido (g)..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>ROSQUEAMENTO</strong></td>
                                                    <td><input type="text" name="tampa_rosqueamento_especificacao" value={formData.tampa_rosqueamento_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="CONF. AMOSTRA" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="tampa_rosqueamento_tolerancia" value={formData.tampa_rosqueamento_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>MANUAL COM TAMPA</td>
                                                    <td><input type="text" name="tampa_rosqueamento_encontrado" value={formData.tampa_rosqueamento_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Adequado / Inadequado..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>VEDAÇÃO</strong></td>
                                                    <td><input type="text" name="tampa_vedacao_especificacao" value={formData.tampa_vedacao_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="CONF. AMOSTRA" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="tampa_vedacao_tolerancia" value={formData.tampa_vedacao_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>MANUAL COM TAMPA</td>
                                                    <td><input type="text" name="tampa_vedacao_encontrado" value={formData.tampa_vedacao_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Eficaz / Ineficaz..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2"><strong>REBÁRBAS</strong></td>
                                                    <td><input type="text" name="tampa_rebarbas_especificacao" value={formData.tampa_rebarbas_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM REBARBAS" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="tampa_rebarbas_tolerancia" value={formData.tampa_rebarbas_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="tampa_rebarbas_encontrado" value={formData.tampa_rebarbas_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Ausente / Presente..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                            </>
                                        )}

                                        {/* RÓTULO */}
                                        {(formData.analysisType === 'Rótulo' || formData.analysisType === 'Revalidação') && (
                                            <>
                                                <tr>
                                                    <td colSpan="6" className={styles.tableCategory}>RÓTULO</td>
                                                </tr>
                                                <tr>
                                                    <td rowSpan="4">ARTE</td>
                                                    <td><strong>CORES</strong></td>
                                                    <td><input type="text" name="rotulo_arte_especificacao" value={formData.rotulo_arte_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="rotulo_arte_tolerancia" value={formData.rotulo_arte_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="PADRÃO" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="rotulo_arte_encontrado" value={formData.rotulo_arte_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Conforme / Não conforme..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>CÓDIGO DE BARRAS</strong></td>
                                                    <td><input type="text" name="rotulo_codigo_especificacao" value={formData.rotulo_codigo_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="rotulo_codigo_tolerancia" value={formData.rotulo_codigo_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="NÃO APLICÁVEL" disabled={!!lastAnalysisData} /></td>
                                                    <td>APP CELULAR / LEITOR</td>
                                                    <td><input type="text" name="rotulo_codigo_encontrado" value={formData.rotulo_codigo_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Legível / Ilegível..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>LEGIBILIDADE</strong></td>
                                                    <td><input type="text" name="rotulo_legibilidade_especificacao" value={formData.rotulo_legibilidade_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="rotulo_legibilidade_tolerancia" value={formData.rotulo_legibilidade_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="rotulo_legibilidade_encontrado" value={formData.rotulo_legibilidade_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Clara / Não clara..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>FALHAS/BORRÕES</strong></td>
                                                    <td><input type="text" name="rotulo_falhas_especificacao" value={formData.rotulo_falhas_especificacao} onChange={handleFormChange} className={styles.inputTable} placeholder="—" disabled={!!lastAnalysisData} /></td>
                                                    <td><input type="text" name="rotulo_falhas_tolerancia" value={formData.rotulo_falhas_tolerancia} onChange={handleFormChange} className={styles.inputTable} placeholder="SEM ERROS" disabled={!!lastAnalysisData} /></td>
                                                    <td>VISUAL</td>
                                                    <td><input type="text" name="rotulo_falhas_encontrado" value={formData.rotulo_falhas_encontrado || ''} onChange={handleFormChange} className={styles.inputTable} placeholder="Ausente / Presente..." disabled={!!lastAnalysisData} /></td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* PARECER FINAL */}
                            <div className={styles.formSection}>
                                <h3 className={styles.sectionTitle}>Conclusão Mestre de Liberação</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.inputGroup}>
                                        <label>Status de Liberação</label>
                                        <select
                                            name="liberacao"
                                            value={formData.liberacao}
                                            onChange={handleFormChange}
                                            className={styles.select}
                                            style={{ fontWeight: 'bold' }}
                                            disabled={!!lastAnalysisData}
                                        >
                                            <option value="Aprovado">Aprovado</option>
                                            <option value="Aprovado com restrição">Aprovado com Restrição</option>
                                            <option value="Reprovado">Reprovado</option>
                                        </select>
                                    </div>

                                    <div className={styles.inputGroup}>
                                        <label>Nova Data de Validade Estendida {(formData.liberacao !== 'Reprovado') && '*'}</label>
                                        <input
                                            type="text"
                                            name="newExpiryDate"
                                            value={formData.newExpiryDate}
                                            onChange={handleFormChange}
                                            className={styles.input}
                                            disabled={formData.liberacao === 'Reprovado' || !!lastAnalysisData}
                                            placeholder="dd/mm/aaaa"
                                            required={formData.liberacao !== 'Reprovado' && !lastAnalysisData}
                                        />
                                        <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                            {formData.liberacao === 'Aprovado' || formData.liberacao === 'Aprovado com restrição'
                                                ? 'Data calculada automaticamente: +5 anos a partir de hoje'
                                                : 'Campo desabilitado para reprovação'}
                                        </small>
                                    </div>
                                </div>

                                <div className={styles.inputGroup} style={{ marginTop: '16px' }}>
                                    <label>Observações / Justificativa Técnica do Inspetor</label>
                                    <textarea
                                        name="observacao"
                                        value={formData.observacao}
                                        onChange={handleFormChange}
                                        className={styles.textarea}
                                        placeholder="Insira detalhes adicionais sobre desvios encontrados, restrições de uso ou motivos de descarte..."
                                        disabled={!!lastAnalysisData}
                                    />
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <button type="button" onClick={handleCloseModal} className={styles.secondaryButton} disabled={submitting || generatingDoc}>
                                    {lastAnalysisData ? 'Fechar' : 'Cancelar'}
                                </button>
                                {lastAnalysisData ? (
                                    <button
                                        type="button"
                                        onClick={() => handleGenerateTemplate(selectedItem)}
                                        className={styles.primaryButton}
                                        disabled={generatingDoc}
                                        style={{
                                            background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)'
                                        }}
                                    >
                                        {generatingDoc ? '⏳ Gerando...' : '⬇ Gerar Análise'}
                                    </button>
                                ) : (
                                    <button type="submit" className={styles.primaryButton} disabled={submitting || generatingDoc}>
                                        {submitting ? 'Registrando Análise...' : 'Concluir Análise'}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PackagingAnalysisPage;